import React, { useState } from 'react';
import { TextField, Button, Paper, Typography, Alert, Box, CircularProgress, Divider } from '@mui/material';
import { Link } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { GoogleLogin } from '@react-oauth/google';
import api from '../api';

export default function Login({ setToken }) {
  const theme = useTheme();
  const custom = theme.palette.custom || {};
  const googleEnabled = Boolean(process.env.REACT_APP_GOOGLE_CLIENT_ID);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const normalizeLoginError = (err) => {
    if (err?.code === 'ECONNABORTED') {
      return "Le serveur met trop de temps à répondre (possible réveil Render). Réessaie dans quelques secondes.";
    }
    if (!err?.response) {
      return "Impossible de contacter le serveur. Vérifie ta connexion et réessaie (le backend est peut-être en réveil).";
    }
    return err.response?.data?.error || 'Erreur de connexion';
  };

  const saveToken = (token) => {
    setToken(token);
    localStorage.setItem('token', token);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setError('');
    setLoading(true);
    try {
      const res = await api.post('/users/login', { email, password });

      if (res.data?.token) {
        saveToken(res.data.token);
      } else {
        setError('Aucun token reçu');
      }
    } catch (err) {
      setError(normalizeLoginError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    if (!credentialResponse?.credential || googleLoading) return;

    setError('');
    setGoogleLoading(true);
    try {
      const res = await api.post('/users/google', {
        credential: credentialResponse.credential
      });

      if (res.data?.token) {
        saveToken(res.data.token);
      } else {
        setError('Aucun token reçu depuis Google');
      }
    } catch (err) {
      setError(normalizeLoginError(err));
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
      <Paper elevation={4} sx={{ p: 4, borderRadius: 3, minWidth: 320, backgroundColor: custom.surfaceLight }}>
        <Typography variant="h5" gutterBottom>Connexion</Typography>

        {googleEnabled && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Connexion rapide avec Gmail ou compte Google académique
            </Typography>
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => setError('Connexion Google impossible.')}
              useOneTap={false}
              text="continue_with"
              shape="pill"
              width="100%"
            />
            {googleLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                <CircularProgress size={20} />
              </Box>
            )}
            <Divider sx={{ my: 2 }}>ou</Divider>
          </Box>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            label="Email"
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            fullWidth
            margin="normal"
            required
            disabled={loading || googleLoading}
          />
          <TextField
            label="Mot de passe"
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            fullWidth
            margin="normal"
            required
            disabled={loading || googleLoading}
          />

          <Button
            type="submit"
            variant="contained"
            disabled={loading || googleLoading}
            sx={{
              mt: 2,
              width: "100%",
              backgroundColor: custom.primaryBg,
              color: custom.primaryColor,
              '&:hover': { backgroundColor: '#dccdf0' }
            }}
          >
            {loading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} />
                Connexion...
              </Box>
            ) : (
              'Se connecter'
            )}
          </Button>

          <Button
            component={Link}
            to="/register"
            fullWidth
            sx={{ mt: 2 }}
            variant="outlined"
            disabled={loading || googleLoading}
          >
            Créer un compte Prof
          </Button>

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </form>
      </Paper>
    </Box>
  );
}